// Template registry. Each template is a pure function (IR, LogoAnalysis) -> Timeline.
import type { AnimationIR, Timeline } from "../ir";
import type { LogoAnalysis } from "../analyze";

import { strokeReveal } from "./stroke_reveal";
import { fadeIn } from "./fade_in";
import { scalePop } from "./scale_pop";
import { slideIn } from "./slide_in";
import { morph } from "./morph";
import { glitchIn } from "./glitch_in";
import { elasticIntro } from "./elastic_intro";
import { cinematicZoom } from "./cinematic_zoom";
import { particleAssembly } from "./particle_assembly";
import { liquidReveal } from "./liquid_reveal";
import { kineticTypography } from "./kinetic_typography";
import { radialReveal } from "./radial_reveal";

export type TemplateFn = (ir: AnimationIR, logo: LogoAnalysis) => Timeline;

export const templates: Record<string, TemplateFn> = {
  stroke_reveal: strokeReveal,
  fade_in: fadeIn,
  scale_pop: scalePop,
  slide_in: slideIn,
  morph,
  glitch_in: glitchIn,
  elastic_intro: elasticIntro,
  cinematic_zoom: cinematicZoom,
  particle_assembly: particleAssembly,
  liquid_reveal: liquidReveal,
  kinetic_typography: kineticTypography,
  radial_reveal: radialReveal,
};

export function compileTimeline(ir: AnimationIR, logo: LogoAnalysis): Timeline {
  const fn = templates[ir.templateId] ?? fadeIn;
  return fn(ir, logo);
}

export const TEMPLATE_INFO: Record<
  string,
  { title: string; blurb: string; bestFor: string[] }
> = {
  stroke_reveal:      { title: "Stroke Reveal",      blurb: "Hand-drawn line-on appearance.",         bestFor: ["wordmark", "outlined marks"] },
  fade_in:            { title: "Fade In",            blurb: "Soft opacity entrance.",                  bestFor: ["any logo"] },
  scale_pop:          { title: "Scale Pop",          blurb: "Punchy scale with overshoot.",            bestFor: ["monograms", "icons"] },
  slide_in:           { title: "Slide In",           blurb: "Directional slide-in entry.",             bestFor: ["wide logos"] },
  morph:              { title: "Morph",              blurb: "Shape-to-shape transformation.",          bestFor: ["multi-layer marks"] },
  glitch_in:          { title: "Glitch In",          blurb: "RGB split + scanline reveal.",            bestFor: ["tech", "gaming"] },
  elastic_intro:      { title: "Elastic Intro",      blurb: "Spring physics bounce.",                  bestFor: ["playful brands"] },
  cinematic_zoom:     { title: "Cinematic Zoom",     blurb: "Slow zoom with parallax.",                bestFor: ["luxury", "editorial"] },
  particle_assembly:  { title: "Particle Assembly",  blurb: "Dust particles converge.",                bestFor: ["any logo"] },
  liquid_reveal:      { title: "Liquid Reveal",      blurb: "Wave mask sweep.",                        bestFor: ["finance", "fluid brands"] },
  kinetic_typography: { title: "Kinetic Typography", blurb: "Per-letter staggered motion.",            bestFor: ["wordmarks"] },
  radial_reveal:      { title: "Radial Reveal",      blurb: "Circular wipe.",                          bestFor: ["circular logos"] },
};
